import React, { useState, useMemo, useEffect } from 'react';
import { Filter, Search, X, Loader2 } from 'lucide-react';
import { useProducts } from '../context/ProductContext';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const INITIAL_VISIBLE = 24;
const LOAD_MORE_STEP = 24;

const MenuPage = () => {
  const { products, categories: apiCategories, loading, error, sync, refreshProducts } = useProducts();
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedStrainType, setSelectedStrainType] = useState('All Strain types');
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedCategory, selectedStrainType, searchQuery, sortBy]);

  const categoryOptions = useMemo(() => {
    const names = apiCategories.length
      ? apiCategories
      : [...new Set(products.map((p) => p.category).filter(Boolean))];
    return ['All Categories', ...names];
  }, [apiCategories, products]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (selectedCategory !== 'All Categories') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (selectedStrainType !== 'All Strain types' && selectedStrainType) {
      filtered = filtered.filter((p) => p.strainType === selectedStrainType);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch (sortBy) {
      case 'price-low':
        filtered = [...filtered].sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'in-stock':
        filtered = [...filtered].sort(
          (a, b) => Number(b.inStock) - Number(a.inStock) || a.name.localeCompare(b.name)
        );
        break;
      default:
        break;
    }

    return filtered;
  }, [products, selectedCategory, selectedStrainType, searchQuery, sortBy]);

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, visibleCount),
    [filteredProducts, visibleCount]
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-neutral-950 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Shop Menu</h1>
          <p className="text-neutral-300 text-lg">
            Availability reflects current stock at Cloud 9, Southampton.
            {sync?.lastSyncAt && (
              <span className="block text-sm text-neutral-400 mt-1">
                Last updated {new Date(sync.lastSyncAt).toLocaleString()}
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside
            className={`lg:w-64 shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}
          >
            <div className="sticky top-28 space-y-6">
              <div>
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filters
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-neutral-700 mb-2 block">
                      Category
                    </label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-neutral-700 mb-2 block">
                      Strain Type
                    </label>
                    <Select value={selectedStrainType} onValueChange={setSelectedStrainType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All Strain types">All Strain types</SelectItem>
                        <SelectItem value="Indica">Indica</SelectItem>
                        <SelectItem value="Sativa">Sativa</SelectItem>
                        <SelectItem value="Hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-950"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="in-stock">In stock first</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="name-asc">Name: A-Z</SelectItem>
                  <SelectItem value="name-desc">Name: Z-A</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? <X className="w-4 h-4 mr-2" /> : <Filter className="w-4 h-4 mr-2" />}
                Filters
              </Button>
              <Button variant="outline" onClick={refreshProducts}>
                Refresh menu
              </Button>
            </div>

            <div className="mb-6 text-sm text-neutral-600">
              Showing {visibleProducts.length} of {filteredProducts.length} products
              {sync?.inStockCount != null && (
                <span> · {sync.inStockCount} in stock at Cloud 9</span>
              )}
            </div>

            {loading ? (
              <div className="py-16 text-center text-neutral-600 flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading menu from Cloud 9...
              </div>
            ) : error && !products.length ? (
              <div className="py-16 text-center">
                <p className="text-red-600 mb-2">{error}</p>
                <p className="text-neutral-600 mb-4">
                  We could not load the latest menu. Try refreshing in a moment.
                </p>
                <Button onClick={refreshProducts}>Try again</Button>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-neutral-500 text-lg">No products found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleProducts.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    priority={index < 4}
                  />
                ))}
              </div>
            )}

            {!loading && visibleProducts.length < filteredProducts.length && (
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  onClick={() =>
                    setVisibleCount((count) =>
                      Math.min(count + LOAD_MORE_STEP, filteredProducts.length)
                    )
                  }
                >
                  Load more products ({filteredProducts.length - visibleProducts.length} remaining)
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
